import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// ═══════════════════════════════════════════════════════════════
// Formation3DView —— 地层 + 井筒 3D 全景 (工程表达版)
//
//   工程要素:  沿轨迹的 MD 刻度环 + 标注 · 分级套管柱 + 套管鞋 · KOP 标记
//              已钻段(按井径变半径, 按扩径率染色) vs 未钻段(细虚线) · 钻头位置
//   交互:      地层剖切面 · 单层显隐 · 垂向拉伸 · 预设视角 · 悬停读数(MD/TVD/井斜/井径)
//   工程约定:  世界坐标 x=东(E), y=向上(=-TVD×垂向拉伸), z=北(N)
//
//   实现要点: 场景只在"几何输入"变化时重建; 钻进进度用 setDrawRange 增量更新,
//            剖切/显隐直接改 Plane.constant 与 mesh.visible, 都不触发重建。
// ═══════════════════════════════════════════════════════════════

export type Layer3D = {
  name: string; top: number; bottom: number; color: string; lith: string; target?: boolean;
  dipAngle?: number; dipAzimuth?: number; extentX?: number; extentY?: number;
};
export type Zone3D = { name: string; top: number; bottom: number; type: string; severity: number };
export type Station3D = { md: number; inc: number; azi: number; tvd: number; ns: number; ew: number };
export type Caliper3D = { depth: number; bitSize: number; caliper: number; washoutRate: number };
export type Casing3D = { name: string; top: number; bottom: number; od: number; bitSize: number; color: string };
export type Fracture3D = {
  id: number; md: number; dip_angle: number; dip_azimuth: number;
  radius_m?: number; aperture_mm?: number | null; frac_type?: string;
};
export type Fault3D = {
  id: number; name: string; ref_east: number; ref_north: number; ref_tvd: number;
  dip_angle: number; dip_azimuth: number; extent_along_strike: number;
  extent_along_dip: number; severity: number; is_sealing?: boolean;
};
export type Horizon3D = {
  id: number; name: string; nx: number; ny: number; dx: number; dy: number;
  origin_east: number; origin_north: number; grid_z: (number | null)[]; color_code?: string;
};

type Props = {
  layers: Layer3D[];
  trajectory: Station3D[];
  zones: Zone3D[];
  caliperData: Caliper3D[];
  casing: Casing3D[];
  currentDepth: number;   // 当前钻进测深 MD (m)
  fractures?: Fracture3D[];   // 天然裂缝 (成像测井/蚂蚁体解释成果)
  faults?: Fault3D[];         // 断层面 (地震构造解释, 带产状)
  horizons?: Horizon3D[];     // 层位面 (网格化解释面)
};

type HoverInfo = {
  x: number; y: number;
  md: number; tvd: number; inc: number; azi: number;
  caliper: number; bitSize: number; washoutRate: number;
  layer?: string;
};

const CSS = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', accent: '#C2533A', panel: 'rgba(255,255,255,0.94)',
};

const ZONE_COLOR: Record<string, number> = {
  fault: 0xC0392B, high_pressure: 0xD4770C, collapse: 0xD4A017,
};

// 扩径率 → 井壁颜色 (与 2D 剖面的分级口径一致)
const CASING_STEEL = new THREE.Color(0x2C3E50);
const WASHOUT_COL = new THREE.Color(0xD4770C);

const VIEWS = [
  { key: 'iso', label: '轴测', dir: new THREE.Vector3(0.85, 0.5, 0.85) },
  { key: 'plan', label: '俯视', dir: new THREE.Vector3(0.001, 1, 0.001) },
  { key: 'ns', label: '南北', dir: new THREE.Vector3(1, 0.12, 0.001) },
  { key: 'ew', label: '东西', dir: new THREE.Vector3(0.001, 0.12, 1) },
] as const;

/** 画一张文字贴图 → Sprite (工程标注). */
function makeLabel(text: string, opts?: { color?: string; bg?: string; bold?: boolean; pad?: number }) {
  const color = opts?.color ?? '#2C3E50';
  const bg = opts?.bg ?? 'rgba(255,255,255,0.92)';
  const font = `${opts?.bold ? 'bold ' : ''}30px "Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif`;
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = font;
  const pad = opts?.pad ?? 14;
  const w = Math.ceil(measure.measureText(text).width) + pad * 2;
  const h = 48;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d')!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(30,42,58,0.18)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, w - 2, h - 2);
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, pad, h / 2 + 1);
  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sp.renderOrder = 20;
  (sp as any).userData.aspect = w / h;
  return sp;
}

/** 按 MD 跨度选整数步长, 使刻度标注控制在 ~8 个以内 (工程图惯例: 100/200/500/1000). */
function tickStep(totalMD: number) {
  for (const s of [100, 200, 250, 500, 1000, 2000]) {
    if (totalMD / s <= 8) return s;
  }
  return 2500;
}

export default function Formation3DView({
  layers, trajectory, zones, caliperData, casing, currentDepth,
  fractures = [], faults = [], horizons = [],
}: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);

  // ── UI 状态 (不触发场景重建的用 ref 通道下发) ──
  const [vex, setVex] = useState(1);                 // 垂向拉伸倍数
  const [clipOn, setClipOn] = useState(false);
  const [clipPos, setClipPos] = useState(0);         // -1..1, 剖切面沿北(Z)推进
  const [showCasing, setShowCasing] = useState(true);
  const [showTicks, setShowTicks] = useState(true);
  const [showFrac, setShowFrac] = useState(true);
  const [showFaults, setShowFaults] = useState(true);
  const [showHorizons, setShowHorizons] = useState(true);
  const [hiddenLayers, setHiddenLayers] = useState<Record<string, boolean>>({});

  // ── 跨 effect 的句柄 ──
  const apiRef = useRef<{
    setDrilled: (md: number) => void;
    setClip: (on: boolean, pos: number) => void;
    setLayerVisible: (name: string, v: boolean) => void;
    setGroupVisible: (g: 'casing' | 'ticks' | 'fractures' | 'faults' | 'horizons', v: boolean) => void;
    lookFrom: (dir: THREE.Vector3) => void;
  } | null>(null);

  // ═══ 场景构建: 只依赖几何输入 + 垂向拉伸 ═══
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    if (!layers.length && trajectory.length < 2) return;

    const W = Math.max(1, el.clientWidth), H = Math.max(1, el.clientHeight);

    // ── 坐标变换: TVD → 世界 Y (向上为正, 含垂向拉伸) ──
    const Y = (tvd: number) => -tvd * vex;
    const worldAt = (s: { ew: number; tvd: number; ns: number }) =>
      new THREE.Vector3(s.ew || 0, Y(s.tvd), s.ns || 0);

    const stations = trajectory;
    const hasTraj = stations.length >= 2;
    const totalMD = hasTraj ? stations[stations.length - 1].md : 0;

    /** 按 MD 线性插值测斜 (井筒上任意点的工程读数). */
    const stationAt = (md: number): Station3D => {
      if (!hasTraj) return { md, inc: 0, azi: 0, tvd: md, ns: 0, ew: 0 };
      if (md <= stations[0].md) return stations[0];
      const last = stations[stations.length - 1];
      if (md >= last.md) return last;
      for (let i = 1; i < stations.length; i++) {
        if (stations[i].md >= md) {
          const a = stations[i - 1], b = stations[i];
          const t = b.md === a.md ? 0 : (md - a.md) / (b.md - a.md);
          const lerp = (p: number, q: number) => p + (q - p) * t;
          // 方位角跨 0/360 时走短弧, 否则插值会出现假的"回转"
          let dAzi = b.azi - a.azi;
          if (dAzi > 180) dAzi -= 360;
          if (dAzi < -180) dAzi += 360;
          return {
            md, inc: lerp(a.inc, b.inc), azi: (a.azi + dAzi * t + 360) % 360,
            tvd: lerp(a.tvd, b.tvd), ns: lerp(a.ns, b.ns), ew: lerp(a.ew, b.ew),
          };
        }
      }
      return last;
    };

    const cal = [...caliperData].sort((a, b) => a.depth - b.depth);
    const calAt = (tvd: number): Caliper3D => {
      if (!cal.length) return { depth: tvd, bitSize: 8.5, caliper: 8.5, washoutRate: 0 };
      if (tvd <= cal[0].depth) return cal[0];
      if (tvd >= cal[cal.length - 1].depth) return cal[cal.length - 1];
      for (let i = 1; i < cal.length; i++) {
        if (cal[i].depth >= tvd) {
          const a = cal[i - 1], b = cal[i], f = (tvd - a.depth) / (b.depth - a.depth || 1);
          return {
            depth: tvd, bitSize: a.bitSize,
            caliper: a.caliper + (b.caliper - a.caliper) * f,
            washoutRate: a.washoutRate + (b.washoutRate - a.washoutRate) * f,
          };
        }
      }
      return cal[cal.length - 1];
    };

    // ── 场景尺度 ──
    const totalTVD = Math.max(
      ...layers.map((l) => l.bottom),
      ...stations.map((s) => s.tvd),
      1000,
    );
    const minExt = totalTVD * 0.85;
    const vexOf = (e?: number) => Math.max(e ?? 2000, minExt);
    const extX = layers.length ? Math.max(...layers.map((l) => vexOf(l.extentX))) : minExt;
    const extZ = layers.length ? Math.max(...layers.map((l) => vexOf(l.extentY))) : minExt;
    const depthSpan = totalTVD * vex;
    const sceneSize = Math.max(extX, extZ, depthSpan);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xFAFBFC);

    const FOV = 42;
    const cam = new THREE.PerspectiveCamera(FOV, W / H, 1, sceneSize * 60);

    // 取景以"井"为主体, 不以地层板为主体 —— 地层板是背景context, 让它溢出画面,
    // 否则一口井会被 3~4 km 见方的板子挤成一根细线 (商业钻井软件的取景惯例)。
    const framePts = hasTraj
      ? stations.map(worldAt)
      : [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -depthSpan, 0)];
    const bbox = new THREE.Box3().setFromPoints(framePts);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    // 井眼半径量级的下限, 免得直井 (水平跨度≈0) 算出 0 包围盒
    const wellFrameR = Math.max(0.5 * Math.hypot(size.x, size.y, size.z), depthSpan * 0.12, 100);
    // 包围球取景: 竖直/水平两个视场角取小的那个, 保证窄窗口下井也完整入画
    const vHalf = (FOV / 2) * Math.PI / 180;
    const hHalf = Math.atan(Math.tan(vHalf) * (W / H));
    const fitDist = (wellFrameR / Math.sin(Math.min(vHalf, hHalf))) * 1.08;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.localClippingEnabled = true;   // 剖切只作用于地层材质
    el.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xFFFFFF, 0.72));
    const key = new THREE.DirectionalLight(0xFFFFFF, 0.7);
    key.position.set(extX, depthSpan, extZ);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xFFFFFF, 0.24);
    fill.position.set(-extX, -depthSpan * 0.4, -extZ);
    scene.add(fill);

    // ── 剖切面: 法向 +Z(北), 沿 Z 推进; 只挂到地层材质上 ──
    const clipPlane = new THREE.Plane(new THREE.Vector3(0, 0, -1), extZ * 0.5);
    const clipHalf = extZ * 0.55;

    // ═══ 1) 地层: 倾斜板 ═══
    const layerMeshes = new Map<string, THREE.Object3D[]>();
    layers.forEach((layer) => {
      const thick = Math.max(1, layer.bottom - layer.top) * vex;
      const ex = vexOf(layer.extentX), ez = vexOf(layer.extentY);
      const dipRad = (layer.dipAngle ?? 0) * Math.PI / 180;
      const dipAz = (layer.dipAzimuth ?? 90) * Math.PI / 180;
      const midY = Y((layer.top + layer.bottom) / 2);

      const geo = new THREE.BoxGeometry(ex, thick, ez);
      const mat = new THREE.MeshPhongMaterial({
        color: new THREE.Color(layer.color),
        transparent: true,
        opacity: layer.target ? 0.5 : 0.3,
        side: THREE.DoubleSide,
        shininess: 18,
        depthWrite: false,            // 半透明地层不写深度 → 井筒不会被随机盖住
        clippingPlanes: [clipPlane],
        clipShadows: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(0, midY, 0);
      // 走向轴 = 水平面内垂直于倾向的方向; 绕它倾转 dip 角
      const strike = new THREE.Vector3(Math.cos(dipAz), 0, -Math.sin(dipAz)).normalize();
      mesh.setRotationFromAxisAngle(strike, dipRad);
      mesh.renderOrder = 1;           // 先于井筒绘制
      scene.add(mesh);

      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({
          color: layer.target ? 0xC2533A : 0x8A96A4,
          transparent: true, opacity: layer.target ? 0.85 : 0.32,
          clippingPlanes: [clipPlane],
        }),
      );
      edges.position.copy(mesh.position);
      edges.quaternion.copy(mesh.quaternion);
      edges.renderOrder = 2;
      scene.add(edges);

      // 3D 里只给目标层挂标注 —— 每层都挂会在斜视角下糊成一团, 层名交给右侧图例
      const objs: THREE.Object3D[] = [mesh, edges];
      if (layer.target) {
        const lab = makeLabel(
          `★ ${layer.name}  ${Math.round(layer.top)}–${Math.round(layer.bottom)}m`,
          { color: '#8B3A2A', bold: true },
        );
        const labH = Math.max(28, wellFrameR * 0.13);
        lab.scale.set(labH * (lab as any).userData.aspect, labH, 1);
        lab.position.set(-ex * 0.3, midY, -ez * 0.3);
        scene.add(lab);
        objs.push(lab);
      }

      layerMeshes.set(layer.name, objs);
    });

    // ═══ 2) 井筒 ═══
    let tubeMesh: THREE.Mesh | null = null;
    let segMD: number[] = [];          // 每个管段对应的 MD, 用于按钻进裁剪
    let radialSeg = 16;
    let bitMark: THREE.Mesh | null = null;
    let pickMesh: THREE.Mesh | null = null;
    let casingRuns: { shoeMD: number; objs: THREE.Object3D[] }[] = [];
    const wbR = Math.max(2.5, wellFrameR * 0.011);

    if (hasTraj) {
      const curvePts = stations.map(worldAt);
      const curve = new THREE.CatmullRomCurve3(curvePts, false, 'catmullrom', 0.2);
      const nSeg = Math.min(600, Math.max(120, stations.length * 6));

      // ── 变半径井筒: 半径随井径扩大率鼓出, 颜色随扩径程度由钢青→橙 ──
      const frames = curve.computeFrenetFrames(nSeg, false);
      const pos: number[] = [], nor: number[] = [], col: number[] = [], idx: number[] = [];
      const P = new THREE.Vector3(), nrm = new THREE.Vector3();
      const AMP = 2.4;
      for (let i = 0; i <= nSeg; i++) {
        const u = i / nSeg;
        curve.getPointAt(u, P);
        const md = stations[0].md + u * (totalMD - stations[0].md);
        segMD.push(md);
        const st = stationAt(md);
        const c = calAt(st.tvd);
        const ratio = c.bitSize > 0 ? c.caliper / c.bitSize : 1;
        const r = Math.max(wbR * 0.55, wbR * (1 + AMP * (ratio - 1)));
        const t = Math.min(1, Math.max(0, (ratio - 1) / 0.5));
        const cc = CASING_STEEL.clone().lerp(WASHOUT_COL, t);
        const N = frames.normals[i], B = frames.binormals[i];
        for (let j = 0; j <= radialSeg; j++) {
          const v = (j / radialSeg) * Math.PI * 2;
          const cs = Math.cos(v), sn = Math.sin(v);
          nrm.set(cs * N.x + sn * B.x, cs * N.y + sn * B.y, cs * N.z + sn * B.z).normalize();
          pos.push(P.x + r * nrm.x, P.y + r * nrm.y, P.z + r * nrm.z);
          nor.push(nrm.x, nrm.y, nrm.z);
          col.push(cc.r, cc.g, cc.b);
        }
      }
      for (let i = 1; i <= nSeg; i++) {
        for (let j = 1; j <= radialSeg; j++) {
          const a = (radialSeg + 1) * (i - 1) + (j - 1), b = (radialSeg + 1) * i + (j - 1);
          const c2 = (radialSeg + 1) * i + j, d = (radialSeg + 1) * (i - 1) + j;
          idx.push(a, b, d, b, c2, d);
        }
      }
      const g = new THREE.BufferGeometry();
      g.setIndex(idx);
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      tubeMesh = new THREE.Mesh(g, new THREE.MeshPhongMaterial({
        vertexColors: true, shininess: 55, specular: 0x555555, side: THREE.DoubleSide,
      }));
      tubeMesh.renderOrder = 10;
      scene.add(tubeMesh);

      // 拾取代理: 真井筒在整井视角下只有 2~3 px 宽, 悬停几乎点不中。
      // 用一根同分段、加粗、不渲染的管子专门接射线, 面索引→环号的换算与主管一致。
      pickMesh = new THREE.Mesh(
        new THREE.TubeGeometry(curve, nSeg, wbR * 4.5, radialSeg, false),
        new THREE.MeshBasicMaterial({ visible: false }),
      );
      pickMesh.visible = true;          // 参与射线, 但材质 visible=false 不出画面
      scene.add(pickMesh);

      // ── 未钻段: 细线 + 半透明, 表示设计轨迹 ──
      const planned = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(curve.getPoints(nSeg)),
        new THREE.LineDashedMaterial({
          color: 0x8598AD, dashSize: wellFrameR * 0.022, gapSize: wellFrameR * 0.016,
          transparent: true, opacity: 0.85,
        }),
      );
      planned.computeLineDistances();
      planned.renderOrder = 9;
      scene.add(planned);

      // ── 井口 ──
      const head = new THREE.Mesh(
        new THREE.CylinderGeometry(wbR * 3.2, wbR * 4.4, wbR * 7, 16),
        new THREE.MeshPhongMaterial({ color: 0x4A5868, shininess: 60 }),
      );
      head.position.copy(worldAt(stations[0])).add(new THREE.Vector3(0, wbR * 3.5, 0));
      scene.add(head);

      // ── 钻头 (随钻进移动) ──
      bitMark = new THREE.Mesh(
        new THREE.SphereGeometry(wbR * 2.6, 18, 12),
        new THREE.MeshPhongMaterial({ color: 0x8B0000, emissive: 0x400000, shininess: 80 }),
      );
      bitMark.renderOrder = 12;
      scene.add(bitMark);

      // ═══ 3) MD 刻度环 + 标注 (工程图惯例) ═══
      const ticks = new THREE.Group();
      ticks.name = 'ticks';
      const step = tickStep(totalMD);
      const ringGeo = new THREE.TorusGeometry(wbR * 2.1, wbR * 0.32, 8, 22);
      const ringMat = new THREE.MeshPhongMaterial({ color: 0x4F6680 });
      for (let md = step; md <= totalMD; md += step) {
        const st = stationAt(md);
        const p = worldAt(st);
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.copy(p);
        // 环面法向对齐井筒切向
        const ahead = worldAt(stationAt(Math.min(totalMD, md + 5)));
        const tan = ahead.clone().sub(p);
        if (tan.lengthSq() > 1e-9) {
          ring.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), tan.normalize());
        }
        ticks.add(ring);

        const lb = makeLabel(`MD ${md}  TVD ${Math.round(st.tvd)}`, { color: '#4F6680' });
        const lh = wellFrameR * 0.05;
        lb.scale.set(lh * (lb as any).userData.aspect, lh, 1);
        lb.position.copy(p).add(new THREE.Vector3(0, lh * 0.9, 0));
        ticks.add(lb);
      }
      scene.add(ticks);

      // ── 造斜点 KOP ──
      const kop = stations.find((s) => s.inc > 5);
      if (kop) {
        const p = worldAt(kop);
        const m = new THREE.Mesh(
          new THREE.ConeGeometry(wbR * 3, wbR * 6, 14),
          new THREE.MeshPhongMaterial({ color: 0x6A4C93 }),
        );
        m.position.copy(p).add(new THREE.Vector3(0, wbR * 5, 0));
        m.rotation.x = Math.PI;
        scene.add(m);
        const lb = makeLabel(`KOP  MD ${Math.round(kop.md)}`, { color: '#6A4C93', bold: true });
        const lh = wellFrameR * 0.058;
        lb.scale.set(lh * (lb as any).userData.aspect, lh, 1);
        lb.position.copy(p).add(new THREE.Vector3(0, lh * 1.6, 0));
        scene.add(lb);
      }

      // ═══ 4) 套管程序: 分级套管柱 + 套管鞋 ═══
      const csgGroup = new THREE.Group();
      csgGroup.name = 'casing';
      const maxOD = Math.max(...casing.map((c) => c.od), 1);
      // 每层套管记下座深: 钻头还没到座深就还没下这层套管, 不该画出来 (与 2D 剖面同口径)
      const csgByShoe: { shoeMD: number; objs: THREE.Object3D[] }[] = [];
      [...casing].sort((a, b) => b.od - a.od).forEach((csg) => {
        // 套管沿轨迹从井口下到座深 (座深按 MD 解释, 与现场一致)
        const from = Math.max(stations[0].md, csg.top);
        const to = Math.min(totalMD, csg.bottom);
        if (to <= from) return;
        const n = Math.max(8, Math.round((to - from) / 40));
        const pts: THREE.Vector3[] = [];
        for (let i = 0; i <= n; i++) pts.push(worldAt(stationAt(from + ((to - from) * i) / n)));
        if (pts.length < 2) return;
        const rOuter = wbR * (0.9 + 1.7 * (csg.od / maxOD));
        const tube = new THREE.Mesh(
          new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), n, rOuter, 18, false),
          new THREE.MeshPhongMaterial({
            color: new THREE.Color(csg.color), transparent: true, opacity: 0.34,
            side: THREE.DoubleSide, shininess: 70, depthWrite: false,
          }),
        );
        tube.renderOrder = 6;
        csgGroup.add(tube);

        // 套管鞋: 座深处的加厚环 + 规格标注
        const shoePos = worldAt(stationAt(to));
        const shoe = new THREE.Mesh(
          new THREE.TorusGeometry(rOuter * 1.28, rOuter * 0.3, 8, 24),
          new THREE.MeshPhongMaterial({ color: 0x1F2D3D, shininess: 90 }),
        );
        shoe.position.copy(shoePos);
        const ahead = worldAt(stationAt(Math.min(totalMD, to + 5)));
        const tan = ahead.clone().sub(shoePos);
        if (tan.lengthSq() > 1e-9) {
          shoe.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), tan.normalize());
        }
        csgGroup.add(shoe);

        const lb = makeLabel(`${csg.od}″ ${csg.name} @${csg.bottom}m`, { color: '#1F2D3D', bold: true });
        const lh = wellFrameR * 0.055;
        lb.scale.set(lh * (lb as any).userData.aspect, lh, 1);
        lb.position.copy(shoePos).add(new THREE.Vector3(0, -lh * 0.9, 0));
        csgGroup.add(lb);

        csgByShoe.push({ shoeMD: csg.bottom, objs: [tube, shoe, lb] });
      });
      scene.add(csgGroup);
      casingRuns = csgByShoe;
    }

    // ═══ 5) 避障区: 深度区间圆柱 ═══
    zones.forEach((z) => {
      const h = Math.max(1, z.bottom - z.top) * vex;
      const c = ZONE_COLOR[z.type] ?? 0xD4770C;
      const r = Math.max(25, sceneSize * 0.016);
      const m = new THREE.Mesh(
        new THREE.CylinderGeometry(r, r, h, 20, 1, true),
        new THREE.MeshPhongMaterial({
          color: c, transparent: true, opacity: 0.3,
          side: THREE.DoubleSide, depthWrite: false,
        }),
      );
      m.position.set(0, Y((z.top + z.bottom) / 2), 0);
      m.renderOrder = 5;
      scene.add(m);

      const lb = makeLabel(`⚠ ${z.name} ${Math.round(z.top)}–${Math.round(z.bottom)}m`, { color: '#B03A2E' });
      const lh = wellFrameR * 0.05;
      lb.scale.set(lh * (lb as any).userData.aspect, lh, 1);
      lb.position.set(r * 1.6, Y((z.top + z.bottom) / 2), 0);
      scene.add(lb);
    });

    // ═══ 5-bis) 天然裂缝 / 断层面 / 层位面 (地质建模成果) ═══
    //
    // 产状 → 法向: 倾角 δ、倾向方位 α (0=北, 90=东, 指最大倾斜下降方向) 的面,
    // 其单位法向 (指上) 为  n = (sinδ·sinα, cosδ, sinδ·cosα)。
    //   δ=0  → n=(0,1,0)   水平面, 法向朝上 ✓
    //   δ=90 → n=(sinα,0,cosα) 直立面, 法向水平指向倾向 ✓
    // 走向向量 s = (cosα, 0, -sinα); 上倾向量 u = n × s。
    //
    // 注: 垂向拉伸 vex≠1 时, 位置按 vex 缩放而产状保持真实, 视觉倾角会与真值不符 ——
    //     这是各类地质软件的通行折中; vex=1 (默认) 时严格正确。
    const dipBasis = (dipDeg: number, aziDeg: number) => {
      const d = dipDeg * Math.PI / 180, a = aziDeg * Math.PI / 180;
      const n = new THREE.Vector3(Math.sin(d) * Math.sin(a), Math.cos(d), Math.sin(d) * Math.cos(a)).normalize();
      const st = new THREE.Vector3(Math.cos(a), 0, -Math.sin(a)).normalize();
      const up = new THREE.Vector3().crossVectors(n, st).normalize();
      return { n, st, up };
    };

    // ── 天然裂缝: 每条一个圆盘, 圆心按 MD 落在井轨迹上 ──
    const fracGroup = new THREE.Group();
    fracGroup.name = 'fractures';
    if (fractures.length && hasTraj) {
      const FRAC_COLOR: Record<string, number> = {
        open: 0x1E8449, filled: 0xB8860B, healed: 0x8598AD,
        induced: 0xC0392B, bedding: 0x6A4C93,
      };
      // 同类型共用材质与几何, 数量上千也只有少量 draw call
      const geoCache = new Map<number, THREE.CircleGeometry>();
      const matCache = new Map<number, THREE.MeshPhongMaterial>();
      const qTmp = new THREE.Quaternion();
      const Zaxis = new THREE.Vector3(0, 0, 1);
      fractures.forEach((f) => {
        const st = stationAt(f.md);
        const r = Math.max(2, f.radius_m ?? 8);
        const key = Math.round(r * 4);
        let geo = geoCache.get(key);
        if (!geo) { geo = new THREE.CircleGeometry(r, 20); geoCache.set(key, geo); }
        const col = FRAC_COLOR[f.frac_type ?? 'open'] ?? 0x1E8449;
        let mat = matCache.get(col);
        if (!mat) {
          mat = new THREE.MeshPhongMaterial({
            color: col, transparent: true, opacity: 0.5,
            side: THREE.DoubleSide, depthWrite: false, shininess: 30,
          });
          matCache.set(col, mat);
        }
        const m = new THREE.Mesh(geo, mat);
        m.position.copy(worldAt(st));
        const { n } = dipBasis(f.dip_angle, f.dip_azimuth);
        m.quaternion.copy(qTmp.setFromUnitVectors(Zaxis, n));
        m.renderOrder = 7;
        fracGroup.add(m);
      });
    }
    scene.add(fracGroup);

    // ── 断层面: 按产状与延展铺一个矩形面 (取代原先"立在原点的竖圆柱") ──
    const faultGroup = new THREE.Group();
    faultGroup.name = 'faults';
    faults.forEach((ft) => {
      const { n, st, up } = dipBasis(ft.dip_angle, ft.dip_azimuth);
      const geo = new THREE.PlaneGeometry(
        Math.max(50, ft.extent_along_strike), Math.max(50, ft.extent_along_dip));
      const col = ft.severity >= 4 ? 0xC0392B : ft.severity >= 3 ? 0xD4770C : 0xD4A017;
      const m = new THREE.Mesh(geo, new THREE.MeshPhongMaterial({
        color: col, transparent: true, opacity: 0.34,
        side: THREE.DoubleSide, depthWrite: false, shininess: 20,
      }));
      m.position.set(ft.ref_east, Y(ft.ref_tvd), ft.ref_north);
      // PlaneGeometry 局部轴: x→走向, y→上倾, z→法向
      m.setRotationFromMatrix(new THREE.Matrix4().makeBasis(st, up, n));
      m.renderOrder = 6;
      faultGroup.add(m);

      const eg = new THREE.LineSegments(
        new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.8 }));
      eg.position.copy(m.position);
      eg.quaternion.copy(m.quaternion);
      faultGroup.add(eg);

      const lb = makeLabel(`${ft.name}  ${ft.dip_angle}°/${ft.dip_azimuth}°`,
        { color: '#8E2A1E', bold: true });
      const lh = wellFrameR * 0.055;
      lb.scale.set(lh * (lb as any).userData.aspect, lh, 1);
      lb.position.copy(m.position).add(new THREE.Vector3(0, lh * 1.2, 0));
      faultGroup.add(lb);
    });
    scene.add(faultGroup);

    // ── 层位面: 网格化解释面 → 曲面 mesh (空值处顶点标记为 NaN 以形成尖灭孔洞) ──
    const horizonGroup = new THREE.Group();
    horizonGroup.name = 'horizons';
    horizons.forEach((h) => {
      const { nx, ny, grid_z } = h;
      if (!nx || !ny || !Array.isArray(grid_z) || grid_z.length !== nx * ny) return;
      const geo = new THREE.PlaneGeometry((nx - 1) * h.dx, (ny - 1) * h.dy, nx - 1, ny - 1);
      const pos = geo.attributes.position as THREE.BufferAttribute;
      // PlaneGeometry 顶点在 XY 平面, 逐点把 z 设为 -TVD, 再整体旋到水平。
      // 尖灭(null)处不能写 NaN —— 那会让 computeBoundingSphere 算出 NaN 半径并持续
      // 刷控制台错误; 改为把该点压到邻近有效高程, 再在索引里剔掉相关三角形形成孔洞。
      let zSum = 0, zCnt = 0;
      for (const z of grid_z) if (z != null) { zSum += Number(z); zCnt++; }
      const zFill = zCnt ? zSum / zCnt : 0;
      for (let i = 0; i < pos.count; i++) {
        const z = grid_z[i];
        pos.setZ(i, -(z == null ? zFill : Number(z)) * vex);
      }
      pos.needsUpdate = true;

      // 只保留四个角点都有效的网格单元 → 空值处自然成为孔洞
      const src = geo.getIndex();
      if (src) {
        const keep: number[] = [];
        for (let t = 0; t < src.count; t += 3) {
          const a = src.getX(t), b = src.getX(t + 1), c = src.getX(t + 2);
          if (grid_z[a] != null && grid_z[b] != null && grid_z[c] != null) keep.push(a, b, c);
        }
        geo.setIndex(keep);
      }
      geo.computeVertexNormals();
      geo.computeBoundingSphere();
      const m = new THREE.Mesh(geo, new THREE.MeshPhongMaterial({
        color: new THREE.Color(h.color_code || '#8B7355'),
        transparent: true, opacity: 0.55, side: THREE.DoubleSide,
        shininess: 15, depthWrite: false, flatShading: false,
      }));
      // 绕 X 轴 -90°: 局部 z(高程) → 世界 y(向上), 局部 y → 世界 z(北)
      m.rotation.x = -Math.PI / 2;
      m.position.set(h.origin_east + (nx - 1) * h.dx / 2, 0, h.origin_north + (ny - 1) * h.dy / 2);
      m.renderOrder = 3;
      horizonGroup.add(m);
    });
    scene.add(horizonGroup);

    // ═══ 6) 地表网格 + 指北 ═══
    const grid = new THREE.GridHelper(sceneSize * 1.5, 20, 0xB4BCC6, 0xDDE2E7);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as any).opacity = 0.6;
    scene.add(grid);

    const axes = new THREE.AxesHelper(Math.max(extX, extZ) * 0.16);
    axes.position.set(-extX * 0.55, 4, -extZ * 0.55);
    scene.add(axes);
    const northLab = makeLabel('N ↑', { color: '#2874A6', bold: true });
    const nh = wellFrameR * 0.07;
    northLab.scale.set(nh * (northLab as any).userData.aspect, nh, 1);
    northLab.position.set(-extX * 0.55, 4, -extZ * 0.55 + Math.max(extX, extZ) * 0.2);
    scene.add(northLab);

    // ═══ 7) 相机 / 控制 ═══
    const controls = new OrbitControls(cam, renderer.domElement);
    controls.target.copy(center);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = wellFrameR * 0.08;
    controls.maxDistance = sceneSize * 6;

    const lookFrom = (dir: THREE.Vector3) => {
      cam.position.copy(center).addScaledVector(dir.clone().normalize(), fitDist);
      cam.up.set(0, 1, 0);
      controls.target.copy(center);
      controls.update();
    };
    lookFrom(VIEWS[0].dir);

    // ═══ 8) 悬停读数: 射线打井筒 → 反算 MD ═══
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let hoverRAF = 0;
    const onMove = (ev: MouseEvent) => {
      if (hoverRAF) return;
      hoverRAF = requestAnimationFrame(() => {
        hoverRAF = 0;
        if (!pickMesh) return;
        const rect = renderer.domElement.getBoundingClientRect();
        ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(ndc, cam);
        const hits = raycaster.intersectObject(pickMesh, false);
        if (!hits.length) { setHover(null); return; }
        // 命中三角形的首顶点索引 → 环号 → MD
        const face = hits[0].face;
        const ring = face ? Math.floor(face.a / (radialSeg + 1)) : 0;
        const md = segMD[Math.min(segMD.length - 1, Math.max(0, ring))];
        const st = stationAt(md);
        const c = calAt(st.tvd);
        const ly = layers.find((l) => st.tvd >= l.top && st.tvd < l.bottom);
        setHover({
          x: ev.clientX - rect.left, y: ev.clientY - rect.top,
          md, tvd: st.tvd, inc: st.inc, azi: st.azi,
          caliper: c.caliper, bitSize: c.bitSize, washoutRate: c.washoutRate,
          layer: ly?.name,
        });
      });
    };
    const onLeave = () => setHover(null);
    renderer.domElement.addEventListener('mousemove', onMove);
    renderer.domElement.addEventListener('mouseleave', onLeave);

    // ═══ 9) 渲染循环 ═══
    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, cam);
    };
    animate();

    // ═══ 10) 尺寸自适应: 面板变化也能跟上, 不只是窗口 ═══
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      if (!w || !h) return;
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(el);

    // ═══ 对外句柄: 这些操作都不需要重建场景 ═══
    apiRef.current = {
      setDrilled: (md: number) => {
        if (tubeMesh && segMD.length) {
          let k = 0;
          while (k < segMD.length && segMD[k] <= md) k++;
          const segs = Math.max(0, k - 1);
          tubeMesh.geometry.setDrawRange(0, segs * radialSeg * 6);
          tubeMesh.visible = segs > 0;
        }
        if (bitMark) {
          const clamped = Math.min(Math.max(md, stations[0]?.md ?? 0), totalMD);
          bitMark.position.copy(worldAt(stationAt(clamped)));
          bitMark.visible = md > (stations[0]?.md ?? 0);
        }
        // 钻头没到座深 → 这层套管还没下, 不显示
        casingRuns.forEach(({ shoeMD, objs }) => {
          const run = md >= shoeMD;
          objs.forEach((o) => { o.visible = run; });
        });
      },
      setClip: (on: boolean, p: number) => {
        clipPlane.constant = on ? clipHalf * p : 1e9;   // 关闭时把平面推到无穷远
      },
      setLayerVisible: (name: string, v: boolean) => {
        layerMeshes.get(name)?.forEach((o) => { o.visible = v; });
      },
      setGroupVisible: (g, v) => {
        const obj = scene.getObjectByName(g);
        if (obj) obj.visible = v;
      },
      lookFrom,
    };

    return () => {
      cancelAnimationFrame(frame);
      if (hoverRAF) cancelAnimationFrame(hoverRAF);
      ro.disconnect();
      renderer.domElement.removeEventListener('mousemove', onMove);
      renderer.domElement.removeEventListener('mouseleave', onLeave);
      controls.dispose();
      scene.traverse((o: any) => {
        o.geometry?.dispose?.();
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
        mats.forEach((m: any) => { m.map?.dispose?.(); m.dispose?.(); });
      });
      renderer.dispose();
      apiRef.current = null;
      el.innerHTML = '';
    };
  }, [layers, trajectory, zones, caliperData, casing, vex, fractures, faults, horizons]);

  // ── 增量更新: 都不触发重建 ──
  useEffect(() => { apiRef.current?.setDrilled(currentDepth); }, [currentDepth, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setClip(clipOn, clipPos); }, [clipOn, clipPos, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setGroupVisible('casing', showCasing); }, [showCasing, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setGroupVisible('ticks', showTicks); }, [showTicks, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setGroupVisible('fractures', showFrac); }, [showFrac, fractures, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setGroupVisible('faults', showFaults); }, [showFaults, faults, layers, trajectory, vex]);
  useEffect(() => { apiRef.current?.setGroupVisible('horizons', showHorizons); }, [showHorizons, horizons, layers, trajectory, vex]);
  useEffect(() => {
    layers.forEach((l) => apiRef.current?.setLayerVisible(l.name, !hiddenLayers[l.name]));
  }, [hiddenLayers, layers, trajectory, vex]);

  const toggleLayer = useCallback((name: string) => {
    setHiddenLayers((h) => ({ ...h, [name]: !h[name] }));
  }, []);

  if (!layers.length && trajectory.length < 2) {
    return (
      <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: CSS.textMuted, fontSize: 12 }}>
        无地层 / 测斜数据
      </div>
    );
  }

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '3px 9px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${active ? CSS.accent : CSS.border}`,
    background: active ? 'rgba(194,83,58,0.08)' : '#FFFFFF',
    color: active ? CSS.accent : CSS.textSec,
  });

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#FAFBFC' }}>
      <div ref={hostRef} style={{ width: '100%', height: '100%' }} />

      {/* ── 左上: 视角 + 显示开关 ── */}
      <div style={{
        position: 'absolute', top: 8, left: 10, display: 'flex', flexDirection: 'column', gap: 6,
        background: CSS.panel, padding: '8px 10px', borderRadius: 5, border: `1px solid ${CSS.border}`,
      }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: CSS.textMuted, marginRight: 2 }}>视角</span>
          {VIEWS.map((v) => (
            <button key={v.key} style={chip(false)} onClick={() => apiRef.current?.lookFrom(v.dir)}>{v.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: CSS.textMuted, marginRight: 2 }}>垂向</span>
          {[1, 2, 4].map((k) => (
            <button key={k} style={chip(vex === k)} onClick={() => setVex(k)}>×{k}</button>
          ))}
          <button style={{ ...chip(showCasing), marginLeft: 6 }} onClick={() => setShowCasing((s) => !s)}>套管</button>
          <button style={chip(showTicks)} onClick={() => setShowTicks((s) => !s)}>刻度</button>
        </div>
        {(fractures.length > 0 || faults.length > 0 || horizons.length > 0) && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: CSS.textMuted, marginRight: 2 }}>地质</span>
            {fractures.length > 0 && (
              <button style={chip(showFrac)} onClick={() => setShowFrac((v) => !v)}>
                天然裂缝 {fractures.length}
              </button>
            )}
            {faults.length > 0 && (
              <button style={chip(showFaults)} onClick={() => setShowFaults((v) => !v)}>
                断层 {faults.length}
              </button>
            )}
            {horizons.length > 0 && (
              <button style={chip(showHorizons)} onClick={() => setShowHorizons((v) => !v)}>
                层位面 {horizons.length}
              </button>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button style={chip(clipOn)} onClick={() => setClipOn((s) => !s)}>剖切</button>
          <input
            type="range" min={-1} max={1} step={0.01} value={clipPos}
            disabled={!clipOn}
            onChange={(e) => setClipPos(Number(e.target.value))}
            style={{ width: 120, accentColor: CSS.accent, opacity: clipOn ? 1 : 0.4 }}
          />
        </div>
      </div>

      {/* ── 右上: 地层图例 (点击显隐) ── */}
      {layers.length > 0 && (
        <div style={{
          position: 'absolute', top: 8, right: 10, maxHeight: 'calc(100% - 90px)', overflowY: 'auto',
          background: CSS.panel, padding: '8px 10px', borderRadius: 5, border: `1px solid ${CSS.border}`,
          minWidth: 150,
        }}>
          <div style={{ fontSize: 10, color: CSS.textMuted, marginBottom: 5 }}>地层图例 · 点击显隐</div>
          {layers.map((l) => {
            const off = !!hiddenLayers[l.name];
            return (
              <div key={l.name} onClick={() => toggleLayer(l.name)} style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                padding: '2px 0', opacity: off ? 0.38 : 1,
              }}>
                <span style={{
                  width: 11, height: 11, borderRadius: 2, background: l.color, flexShrink: 0,
                  border: l.target ? `2px solid ${CSS.accent}` : '1px solid rgba(0,0,0,0.15)',
                }} />
                <span style={{
                  fontSize: 11, color: l.target ? CSS.accent : CSS.textSec,
                  fontWeight: l.target ? 700 : 400,
                  textDecoration: off ? 'line-through' : 'none',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{l.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 左下: 坐标约定 ── */}
      <div style={{
        position: 'absolute', bottom: 8, left: 10, fontSize: 10, color: CSS.textSec,
        background: CSS.panel, padding: '5px 9px', borderRadius: 4,
        border: `1px solid ${CSS.border}`, lineHeight: 1.65,
      }}>
        左键旋转 · 滚轮缩放 · 右键平移 · 悬停井筒读数<br />
        红=东(X) · 绿=上(Y) · 蓝=北(Z)
        {vex > 1 && <span style={{ color: CSS.accent, fontWeight: 600 }}> · 垂向拉伸 ×{vex}</span>}
      </div>

      {/* ── 悬停读数卡 ── */}
      {hover && (
        <div style={{
          position: 'absolute', left: Math.min(hover.x + 16, 9999), top: hover.y + 14,
          pointerEvents: 'none', background: 'rgba(255,255,255,0.97)',
          border: `1px solid ${CSS.accent}`, borderRadius: 5, padding: '7px 10px',
          fontSize: 11, color: CSS.text, fontFamily: 'monospace', lineHeight: 1.7,
          boxShadow: '0 3px 12px rgba(30,42,58,0.14)', whiteSpace: 'nowrap', zIndex: 5,
        }}>
          <div style={{ fontWeight: 700, color: CSS.accent, marginBottom: 2 }}>
            MD {Math.round(hover.md)} m
          </div>
          <div>TVD&nbsp; {Math.round(hover.tvd)} m</div>
          <div>井斜&nbsp; {hover.inc.toFixed(1)}° · 方位 {hover.azi.toFixed(0)}°</div>
          <div>井径&nbsp; {hover.caliper.toFixed(1)}″ / 钻头 {hover.bitSize}″</div>
          <div style={{ color: hover.washoutRate > 10 ? '#C0392B' : CSS.textSec }}>
            扩径率 {hover.washoutRate.toFixed(1)}%
          </div>
          {hover.layer && <div style={{ color: CSS.textSec }}>层位&nbsp; {hover.layer}</div>}
        </div>
      )}
    </div>
  );
}
